import { useEffect } from "react";

import { useFormContext, useFormState, useWatch } from "react-hook-form";

import { BlockConfigCard } from "src/cases/templates/caseBlocks/BlockConfigCard";
import {
  useCaseBlocksConfigActions,
  useSelectedBlockId,
} from "src/cases/templates/caseBlocks/CaseBlocksConfigContext";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";
import { useCheckFieldPermission } from "src/cases/templates/useCheckFieldPermission";
import { useScrollIntoView } from "src/utils/useScrollIntoView";

type BlockCardProps = {
  blockInternalId: string;
  onDelete: () => void;
};

export const BlockCard: React.FC<BlockCardProps> = ({
  blockInternalId,
  onDelete,
}) => {
  const blockIndex = useBlockIndex(blockInternalId);
  const blockPath = `attributes.case_blocks_configs.${blockIndex}` as const;

  const { control, getValues } = useFormContext<CaseTemplateFormValues>();
  const blockConfig = getValues(blockPath);

  const { errors } = useFormState({ control, name: blockPath });
  const selectedBlockId = useSelectedBlockId();
  const { selectBlock } = useCaseBlocksConfigActions();
  const isSelected = selectedBlockId === blockInternalId;

  const { ref, scrollIntoView } = useScrollIntoView({
    delay: 25,
    block: "center",
    visibilityMargin: 20,
  });

  useEffect(() => {
    if (isSelected) {
      scrollIntoView();
    }
  }, [isSelected, scrollIntoView]);

  const hasPermission = useCheckFieldPermission();
  const canDelete = hasPermission("delete", "attributes.case_blocks_configs");

  const hasErrors =
    blockIndex !== -1 &&
    Boolean(errors.attributes?.case_blocks_configs?.[blockIndex]);

  const handleDelete = () => {
    selectBlock(null);
    onDelete();
  };

  if (!blockConfig) {
    return null;
  }

  return (
    <BlockConfigCard
      ref={ref}
      config={blockConfig}
      errored={hasErrors}
      isSelected={isSelected}
      onDelete={canDelete ? handleDelete : undefined}
      onSelect={() => selectBlock(isSelected ? null : blockInternalId)}
    />
  );
};

const useBlockIndex = (blockInternalId: string) => {
  const { control } = useFormContext<CaseTemplateFormValues>();
  return useWatch({
    control,
    name: "attributes.case_blocks_configs",
    compute: (value) =>
      (value ?? []).findIndex((b) => b._internalId === blockInternalId),
  });
};
